import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../Security/firebase-config";
import { collection, getDoc, getDocs, doc } from "firebase/firestore";
import { useTeam } from "../../context/TeamContext";
import { VALORANT_MAPS } from "./MapFolderList";

import "./ReviewOverviewPanel.css";

function safeToMillis(ts) {
  // Firestore Timestamp
  if (ts?.toDate) return ts.toDate().getTime();
  // already millis
  if (typeof ts === "number") return ts;
  // { seconds, nanoseconds }
  if (typeof ts?.seconds === "number") return ts.seconds * 1000;
  return null;
}

function formatDate(millis) {
  if (!millis) return "—";
  const d = new Date(millis);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
}

// Reuse your existing “relevant to me” definition from VideoList:
// @Team OR @username and not viewed by me. (Replies: not viewed by me and not my reply)
// (This matches the logic shown in VideoList.js.):contentReference[oaicite:2]{index=2}
async function computeUnreviewedForMatch({ teamId, mapId, matchId, username }) {
  if (!teamId || !mapId || !matchId || !username) return 0;

  let unreviewed = 0;

  // 1) Notes
  const notesRef = collection(db, "teams", teamId, "maps", mapId, "matches", matchId, "notes");
  const notesSnap = await getDocs(notesRef);

  for (const noteDoc of notesSnap.docs) {
    const note = noteDoc.data() || {};
    const mentions = Array.isArray(note.at) ? note.at : [];

    const relevantToUser =
      mentions.includes("@Team") || mentions.includes(`@${username}`);

    const userHasNotViewed =
      !Array.isArray(note.viewers) || !note.viewers.includes(username);

    if (relevantToUser && userHasNotViewed) unreviewed += 1;
  }

  // 2) Replies
  const repliesRef = collection(db, "teams", teamId, "maps", mapId, "matches", matchId, "allReplies");
  const repliesSnap = await getDocs(repliesRef);

  for (const rDoc of repliesSnap.docs) {
    const reply = rDoc.data() || {};
    const viewers = Array.isArray(reply.viewers) ? reply.viewers : [];
    const notViewedByMe = !viewers.includes(username);
    const notMyReply = reply.createdBy !== username;

    if (notViewedByMe && notMyReply) unreviewed += 1;
  }

  return unreviewed;
}

async function computeUnreviewedForLegacyVideo({ teamId, mapId, videoId, username }) {
  if (!teamId || !mapId || !videoId || !username) return 0;

  let unreviewed = 0;

  const notesRef = collection(db, "teams", teamId, "maps", mapId, "videos", videoId, "notes");
  const notesSnap = await getDocs(notesRef);

  for (const noteDoc of notesSnap.docs) {
    const note = noteDoc.data() || {};
    const mentions = Array.isArray(note.at) ? note.at : [];

    const relevantToUser =
      mentions.includes("@Team") || mentions.includes(`@${username}`);

    const userHasNotViewed =
      !Array.isArray(note.viewers) || !note.viewers.includes(username);

    if (relevantToUser && userHasNotViewed) unreviewed += 1;
  }

  const repliesRef = collection(db, "teams", teamId, "maps", mapId, "videos", videoId, "allReplies");
  const repliesSnap = await getDocs(repliesRef);

  for (const rDoc of repliesSnap.docs) {
    const reply = rDoc.data() || {};
    const viewers = Array.isArray(reply.viewers) ? reply.viewers : [];
    const notViewedByMe = !viewers.includes(username);
    const notMyReply = reply.createdBy !== username;

    if (notViewedByMe && notMyReply) unreviewed += 1;
  }

  return unreviewed;
}

export default function ReviewOverviewPanel({ onOpenVideo }) {
  const { teamId, username } = useTeam();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  // Filters
  const [sortDir, setSortDir] = useState("desc"); // match date
  const [mapFilter, setMapFilter] = useState("ALL");
  const [includeZero, setIncludeZero] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!teamId || !username) {
        setRows([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        // 1) Fetch visible videos for every map
        const perMapVideos = await Promise.all(
          VALORANT_MAPS.map(async (mapId) => {
            try {
              const vidsRef = collection(db, "teams", teamId, "maps", mapId, "videos");
              const snap = await getDocs(vidsRef);

              const raw = snap.docs.map((d) => ({ id: d.id, ...d.data(), mapId }));

              // match VideoList behavior: hide scaffold videos with no media
              const visible = raw.filter(
                (v) =>
                  !!v.storagePath ||
                  !!v.videoURL ||
                  !!v.url ||
                  (v.playbackMode === "segment_playlist" && v.status !== "recording") ||
                  v.status === "processing" ||
                  v.status === "failed"
              );

              return { mapId, videos: visible };
            } catch {
              return { mapId, videos: [] };
            }
          })
        );

        // 2) Build sets for matchIds, and prepare row shells
        const matchIdsByMap = new Map(); // mapId -> Set(matchId)
        const baseRows = [];

        perMapVideos.forEach(({ mapId, videos }) => {
          const set = new Set();
          videos.forEach((v) => {
            if (v.matchId) set.add(v.matchId);
            baseRows.push({
              mapId,
              mapName: mapId,
              videoId: v.id,
              videoTitle: (v.title || "").trim() || "Untitled",
              povName: (v.povName || "").trim(),
              matchId: v.matchId || null,
              uploadAtMs: safeToMillis(v.createdAt) || null,
              matchAtMs: null,
              unreviewed: 0,
            });
          });
          matchIdsByMap.set(mapId, set);
        });

        // 3) Fetch match docs for match dates/titles (optional) and compute unreviewed per match
        const unreviewedByMatchKey = new Map(); // `${mapId}::${matchId}` -> count
        const matchDateByKey = new Map();       // `${mapId}::${matchId}` -> ms

        await Promise.all(
          Array.from(matchIdsByMap.entries()).map(async ([mapId, set]) => {
            const matchIds = Array.from(set);

            // Fetch match doc dates (matchPlayedAt OR createdAt)
            await Promise.all(
              matchIds.map(async (mid) => {
                try {
                  const mSnap = await getDoc(doc(db, "teams", teamId, "maps", mapId, "matches", mid));
                  if (mSnap.exists()) {
                    const data = mSnap.data() || {};
                    const ms = safeToMillis(data.matchPlayedAt) || safeToMillis(data.createdAt) || null;
                    matchDateByKey.set(`${mapId}::${mid}`, ms);
                  }
                } catch {}
              })
            );

            // Compute unreviewed counts
            await Promise.all(
              matchIds.map(async (mid) => {
                const c = await computeUnreviewedForMatch({ teamId, mapId, matchId: mid, username });
                unreviewedByMatchKey.set(`${mapId}::${mid}`, c);
              })
            );
          })
        );

        // 4) Legacy counts (videos with no matchId)
        const legacy = baseRows.filter((r) => !r.matchId);
        const legacyCounts = await Promise.all(
          legacy.map(async (r) => {
            const c = await computeUnreviewedForLegacyVideo({
              teamId,
              mapId: r.mapId,
              videoId: r.videoId,
              username,
            });
            return { key: `${r.mapId}::${r.videoId}`, c };
          })
        );
        const legacyByKey = new Map(legacyCounts.map((x) => [x.key, x.c]));

        // 5) Merge into final rows
        const finalRows = baseRows.map((r) => {
          if (r.matchId) {
            const key = `${r.mapId}::${r.matchId}`;
            return {
              ...r,
              matchAtMs: matchDateByKey.get(key) || null,
              unreviewed: unreviewedByMatchKey.get(key) ?? 0,
            };
          }
          const key = `${r.mapId}::${r.videoId}`;
          return {
            ...r,
            matchAtMs: null,
            unreviewed: legacyByKey.get(key) ?? 0,
          };
        });

        if (!cancelled) setRows(finalRows);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [teamId, username]);

  const mapOptions = useMemo(() => ["ALL", ...VALORANT_MAPS], []);

  const filteredSorted = useMemo(() => {
    let out = rows.slice();

    if (mapFilter !== "ALL") out = out.filter((r) => r.mapId === mapFilter);
    if (!includeZero) out = out.filter((r) => (r.unreviewed || 0) > 0);

    out.sort((a, b) => {
      // Primary sort: match date (nulls last). Fallback: upload date. Then title.
      const aKey = a.matchAtMs ?? a.uploadAtMs ?? 0;
      const bKey = b.matchAtMs ?? b.uploadAtMs ?? 0;

      const diff = aKey - bKey;
      if (diff !== 0) return sortDir === "asc" ? diff : -diff;

      return (a.videoTitle || "").localeCompare(b.videoTitle || "");
    });

    return out;
  }, [rows, mapFilter, includeZero, sortDir]);

  return (
    <aside className="ReviewOverviewPanel" aria-label="Overview (unreviewed notes)">
      <div className="ReviewOverviewPanel__header">
        <div className="ReviewOverviewPanel__title">Overview</div>

        <div className="ReviewOverviewPanel__filters" role="group" aria-label="Overview filters">
          <button
            type="button"
            className="ReviewOverviewPanel__filterBtn"
            onClick={() => setSortDir((v) => (v === "asc" ? "desc" : "asc"))}
            title="Toggle match date sorting"
          >
            Match Date: {sortDir === "asc" ? "↑" : "↓"}
          </button>

          <label className="ReviewOverviewPanel__selectLabel">
            <span className="ReviewOverviewPanel__selectText">Map</span>
            <select
              className="ReviewOverviewPanel__select"
              value={mapFilter}
              onChange={(e) => setMapFilter(e.target.value)}
            >
              {mapOptions.map((m) => (
                <option key={m} value={m}>
                  {m === "ALL" ? "All Maps" : m}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            className={`ReviewOverviewPanel__filterBtn ${includeZero ? "" : "isActive"}`}
            onClick={() => setIncludeZero((v) => !v)}
            title="Hide/show videos with 0 unreviewed relevant items"
          >
            {includeZero ? "Hide 0s" : "Showing > 0"}
          </button>
        </div>
      </div>

      <div className="ReviewOverviewPanel__body">
        {loading ? (
          <div className="ReviewOverviewPanel__loading">
            <div className="ReviewOverviewPanel__skel" />
            <div className="ReviewOverviewPanel__skel" />
            <div className="ReviewOverviewPanel__skel" />
            <div className="ReviewOverviewPanel__skel" />
          </div>
        ) : filteredSorted.length === 0 ? (
          <div className="ReviewOverviewPanel__empty">
            No videos match your filters.
          </div>
        ) : (
          <div className="ReviewOverviewPanel__list" role="list">
            {filteredSorted.map((r) => (
              <button
                key={`${r.mapId}:${r.videoId}`}
                type="button"
                className="ReviewOverviewPanel__row"
                role="listitem"
                onClick={() => onOpenVideo?.(r.mapId, r.mapName, r.videoId)}
                title="Open this video"
              >
                <div className="ReviewOverviewPanel__rowMain">
                  <div className="ReviewOverviewPanel__videoTitle">
                    {r.videoTitle}
                    {r.povName ? <span className="ReviewOverviewPanel__pov"> • {r.povName}</span> : null}
                  </div>

                  <div className="ReviewOverviewPanel__meta">
                    <span className="ReviewOverviewPanel__metaItem">🗺 {r.mapName}</span>
                    <span className="ReviewOverviewPanel__metaItem">
                      📅 Match: {formatDate(r.matchAtMs)}
                    </span>
                    <span className="ReviewOverviewPanel__metaItem">
                      ⬆ Upload: {formatDate(r.uploadAtMs)}
                    </span>
                  </div>
                </div>

                <div
                  className={`ReviewOverviewPanel__badge ${r.unreviewed > 0 ? "needsReview" : "allClear"}`}
                  title={
                    r.unreviewed > 0
                      ? `${r.unreviewed} unreviewed relevant notes/replies`
                      : "No unreviewed relevant notes/replies"
                  }
                >
                  {r.unreviewed}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}
